// eslint-disable  @typescript-eslint/no-inferrable-types
import {
  AbstractType,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  InjectionToken,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Type,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { get, isFunction, isNull, isNumber, trim } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, shareReplay, startWith } from 'rxjs/operators';
import {
  ComponentStaticText,
  GridView,
  ToolbarButtonDefinition,
  XuxGridAction,
  XuxGridOptions,
} from '@geniuspos/ui-shared';
import { GridRowHeightName } from '@geniuspos/ui-shared/index';
import { BUTTON_FLEX_ORDER } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/constants';
import { XUX_GRID_ACTIONS } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/grid-action.type';
import {
  DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
  DEFAULT_FORM_UPDATE_DELAY_MS,
} from '../../../config';
import { UiActions } from '@app/store/actions';
import { EInputEvent, IArtificialInputWatcherOption } from '@app/shared/types';
import { artificialInputWatcher } from '@app/shared/utils';
import { ScanningService } from '@app/shared/services/scanning/scanning.service';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';

@Component({
  selector: 'xux-grid-actions-panel-custom',
  templateUrl: './xux-grid-actions-panel-custom.component.html',
  styleUrls: ['./xux-grid-actions-panel-custom.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxGridActionsPanelCustomComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @ViewChild('quickFilterInput', { static: false })
  public quickFilterInput: ElementRef;
  @Output() public action: EventEmitter<XuxGridAction> =
    new EventEmitter<XuxGridAction>();
  @Input() public options: XuxGridOptions;
  @Input() public view: GridView;
  @Input() public buttons: ToolbarButtonDefinition[];
  @Input() public pageCount: number;
  @Input() public searchPlaceholder: string;
  @Input() public gridRowHeight: GridRowHeightName;
  @Input() public showSearch: boolean;
  @Input() public allCollapsed: boolean;
  @Input() public disabled: boolean = false;
  @Input() public artificialInputCheckForSearchPanel: boolean = false;

  @Input()
  public set i18n(staticTextContent: ComponentStaticText) {
    this.staticTextContent = {
      ...this.staticTextContent,
      ...(staticTextContent || {}),
    };
  }

  @lazyInject(ScanningService) public scanningService: ScanningService;

  public staticTextContent: ComponentStaticText = {};
  public quickFilterControl: FormControl = new FormControl('');
  public quickFilterControlValue$: Observable<string | null>;

  public buttonFlexOrder: { [key: string]: number | string } =
    BUTTON_FLEX_ORDER;
  public keyTimes: number[] = [];

  public cancelSubscription$ = new Subject<void>();

  private subscriptions: Subscription[] = [];

  protected get artificialInputThreshold() {
    return get(
      this.artificialInputCheckForSearchPanel,
      'artificialInputThreshold',
      DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
    );
  }

  public constructor(
    public uiActions: UiActions,
    public injector: Injector,
  ) {}

  public ngOnInit(): void {
    this.initQuickFilterDebounce();
    this.initStreams();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public ngAfterViewInit(): void {
    if (this.artificialInputCheckForSearchPanel) {
      const artificialInputWatcherOption: IArtificialInputWatcherOption = {
        nativeElement: this.quickFilterInput.nativeElement,
        cancelSubscription$: this.cancelSubscription$,
        artificialInputThreshold: this.artificialInputThreshold,
      };

      artificialInputWatcher(
        EInputEvent.KEYDOWN,
        {
          ...artificialInputWatcherOption,
          updateValueBeforeEventHandler: true,
          scanningService: this.scanningService,
        },
        this.setNewValue.bind(this),
      );
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('view' in changes) {
      const quickFilterValue = get(this.view, 'quickFilter', '');
      if (this.quickFilterControl.value !== quickFilterValue) {
        this.quickFilterControl.setValue(quickFilterValue);
      }
    }
  }

  public ngOnDestroy(): void {
    this.cancelSubscription$.next();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public emitAction(event: XuxGridAction): void {
    this.action.emit(event);
  }

  public quickFilterChanged(): void {
    const value = trim(this.quickFilterControl.value);
    const initialQuickFilterValue = get(this.view, 'quickFilter', null);
    const quickFilterValue = isNull(initialQuickFilterValue)
      ? initialQuickFilterValue
      : trim(initialQuickFilterValue);

    if (!this.showSearch || value === quickFilterValue) return;

    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED,
      payload: { value },
    };
    this.emitAction(gridAction);
  }

  public actionButtonClick(value: MouseEvent): void {
    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.ACTION_BUTTON_CLICKED,
      payload: {
        value,
      },
    };
    this.emitAction(gridAction);
  }

  public setNewValue(
    event: KeyboardEvent,
    artificialInputWatcherOption: IArtificialInputWatcherOption,
    newValue: string,
  ): void {
    const { updateValueBeforeEventHandler, isValidScanning } =
      artificialInputWatcherOption;
    if (updateValueBeforeEventHandler && !isValidScanning) {
      this.quickFilterControl.setValue(newValue);
    }
  }

  /**
   * Initializes quickfilter debounce.
   * Uses default form update timeout when no timeout provided.
   * If a non-numeric value is provided (e.g. null) - no events will be fired during typing
   */
  private initQuickFilterDebounce(): void {
    const searchDebounceTime = get(
      this.options,
      'searchDebounceTime',
      DEFAULT_FORM_UPDATE_DELAY_MS,
    );

    if (isNumber(searchDebounceTime)) {
      this.subscriptions.push(
        this.quickFilterControl.valueChanges
          .pipe(debounceTime(searchDebounceTime))
          .subscribe(() => this.quickFilterChanged()),
      );
    }
  }

  private initStreams(): void {
    this.quickFilterControlValue$ = this.quickFilterControl.valueChanges.pipe(
      startWith(this.quickFilterControl.value as string | null),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
